<template>
  <v-card id="workflow-list">
    <v-card-title class="text-h6">
      Workflows
    </v-card-title>

    <v-card-text>
      <v-alert
        v-if="summaryError"
        type="error"
        dense
        text
        class="mb-0"
      >
        {{ summaryError }}
      </v-alert>

      <div
        v-else-if="summaryLoaded"
        class="d-flex flex-wrap workflow-list__totals"
      >
        <v-chip
          v-for="total in totals"
          :key="total.text"
          small
          label
          :color="total.color"
          class="v-chip-light-bg"
          :class="`${total.color}--text`"
        >
          {{ total.text }}: {{ total.count }}
        </v-chip>
      </div>
    </v-card-text>

    <v-card-text class="pb-0">
      <v-row dense>
        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.status"
            :items="statusItems"
            dense
            outlined
            hide-details
            label="Статус"
          ></v-select>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.definition"
            :items="definitionItems"
            dense
            outlined
            hide-details
            label="Тип"
          ></v-select>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-menu
            v-model="datesOpened"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                :value="datesLabel"
                readonly
                dense
                outlined
                hide-details
                clearable
                label="Период"
                :prepend-inner-icon="icons.mdiCalendarRange"
                v-bind="attrs"
                v-on="on"
                @click:clear="clearDates"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dates"
              range
              locale="ru"
              first-day-of-week="1"
              @change="applyDates"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col
          cols="12"
          md="3"
          class="d-flex align-center"
        >
          <v-switch
            v-model="filters.onlyStale"
            dense
            hide-details
            class="mt-0"
            label="Только зависшие"
          ></v-switch>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text>
      <div class="d-flex align-center flex-wrap">
        <v-btn
          outlined
          small
          :loading="loading"
          @click="refresh"
        >
          <v-icon
            size="18"
            class="me-2"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          <span>Обновить</span>
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn
          color="error"
          small
          :loading="terminating"
          :disabled="!canTerminate"
          @click="terminatePopupShow"
        >
          <span>{{ terminateText }}</span>
        </v-btn>
      </div>

      <v-alert
        v-if="terminateResult"
        :type="terminateResult.type"
        dense
        text
        dismissible
        class="mt-4 mb-0"
        @input="clearTerminateResult"
      >
        {{ terminateResult.text }}
      </v-alert>

      <v-alert
        v-if="error"
        type="error"
        dense
        text
        class="mt-4 mb-0"
      >
        {{ error }}
      </v-alert>
    </v-card-text>

    <v-data-table
      :headers="headers"
      :items="workflows"
      :server-items-length="totalCount"
      :loading="loading"
      :page.sync="filters.pageNumber"
      :items-per-page.sync="filters.pageSize"
      :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
      disable-sort
      class="text-no-wrap"
    >
      <template #[`item.createdAt`]="{item}">
        {{ toDateTimeString(item.createdAt) }}
      </template>

      <template #[`item.status`]="{item}">
        <v-chip
          small
          :color="status(item.status).color"
          text-color="white"
        >
          {{ status(item.status).text }}
        </v-chip>
        <v-chip
          v-if="item.isStale"
          small
          outlined
          color="error"
          class="ms-2"
        >
          зависший
        </v-chip>
      </template>

      <template #[`item.nextRunAt`]="{item}">
        {{ toDateTimeString(item.nextRunAt) || '—' }}
      </template>

      <template #[`item.completedAt`]="{item}">
        {{ toDateTimeString(item.completedAt) || '—' }}
      </template>

      <template #[`item.orderId`]="{item}">
        <router-link
          v-if="item.orderId"
          :to="{ name: 'order-form-tab', params: { id: item.orderId } }"
        >
          {{ item.orderId }}
        </router-link>
        <span v-else>—</span>
      </template>

      <template #[`item.id`]="{item}">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <span
              class="workflow-list__id"
              v-bind="attrs"
              v-on="on"
              @click="copyId(item.id)"
            >{{ shortId(item.id) }}</span>
          </template>
          <span>{{ item.id }} — нажмите, чтобы скопировать</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-card-text
      v-if="summaryLoaded"
      class="text--secondary"
    >
      Зависшим считается активный workflow {{ staleAge }}.
    </v-card-text>

    <delete-dialog
      ref="terminatePopup"
      :label="terminateLabel"
      @delete-confirmed="terminateStale"
    />
  </v-card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { mdiCalendarRange, mdiRefresh } from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'
import DeleteDialog from '@/components/delete-dialog'
import { hasPermission, toDateTimeString } from '@/helpers'
import { workflowStatus, workflowStatusItems } from '@/helpers/workflow'

const SHORT_ID_LENGTH = 8

export default {
  name: 'WorkflowList',
  components: { DeleteDialog },
  data: () => ({
    icons: { mdiCalendarRange, mdiRefresh },
    toDateTimeString,
    statusItems: workflowStatusItems,
    datesOpened: false,
    dates: [],
  }),
  computed: {
    ...mapFormHandlers('workflow', [
      'filters.pageNumber',
      'filters.pageSize',
      'filters.status',
      'filters.definition',
      'filters.dateFrom',
      'filters.dateTo',
      'filters.onlyStale',
    ]),
    ...mapState('workflow', {
      workflows: 'list',
      totalCount: 'totalCount',
      loading: 'loading',
      error: 'error',
      summary: 'summary',
      summaryLoaded: 'summaryLoaded',
      summaryError: 'summaryError',
      terminating: 'terminating',
      terminateResult: 'terminateResult',
    }),
    datesLabel() {
      const { dateFrom, dateTo } = this.filters

      if (!dateFrom) return ''

      return dateFrom === dateTo ? dateFrom : `${dateFrom} — ${dateTo}`
    },
    definitionItems() {
      return [
        { text: 'Все', value: null },
        ...this.summary.definitions.map(value => ({ text: value, value })),
      ]
    },
    totals() {
      const {
        runnable, suspended, complete, terminated, stale,
      } = this.summary

      return [
        { text: 'Активных', count: runnable + suspended, color: 'info' },
        { text: 'из них зависших', count: stale, color: stale ? 'error' : 'secondary' },
        { text: 'Завершённых', count: complete, color: 'success' },
        { text: 'Отменённых', count: terminated, color: 'secondary' },
      ]
    },

    staleAge() {
      const { staleAfterHours } = this.summary

      return staleAfterHours === 24 ? 'старше суток' : `старше ${staleAfterHours} ч`
    },
    canTerminate() {
      return this.summary.stale > 0 && hasPermission('Permissions.WorkflowTerminate')
    },
    terminateText() {
      return this.summaryLoaded ? `Отменить зависшие (${this.summary.stale})` : 'Отменить зависшие'
    },

    terminateLabel() {
      const { stale, terminatedAtOnce } = this.summary
      const atOnce = terminatedAtOnce > 0 && stale > terminatedAtOnce
        ? `За один раз отменяется не больше ${terminatedAtOnce}, начиная с самых старых: `
          + 'остальные — следующим нажатием. '
        : ''

      return `Будут отменены активные workflow ${this.staleAge} — сейчас их ${stale}. ${atOnce}`
        + 'Вернуть отменённые в работу будет нельзя.'
    },

    headers() {
      return [
        { text: 'Создан', value: 'createdAt' },
        { text: 'Тип', value: 'definition' },
        { text: 'Заказ', value: 'orderId' },
        { text: 'Статус', value: 'status' },
        { text: 'Следующий запуск', value: 'nextRunAt' },
        { text: 'Завершён', value: 'completedAt' },
        { text: 'Идентификатор', value: 'id' },
      ]
    },
  },
  async created() {
    document.title = 'Workflows'

    this.dates = [this.filters.dateFrom, this.filters.dateTo].filter(x => x)
    await this.refresh()
  },
  methods: {
    ...mapActions('workflow', ['fetchList', 'fetchSummary', 'terminateStale']),
    ...mapActions('message', { success: 'success', info: 'info' }),
    ...mapMutations('workflow', { setTerminateResult: 'SET_TERMINATE_RESULT' }),
    clearTerminateResult() {
      this.setTerminateResult(null)
    },
    async refresh() {
      await this.fetchList()
      await this.fetchSummary()
    },
    status(value) {
      return workflowStatus(value)
    },
    shortId(id) {
      return id.length > SHORT_ID_LENGTH ? `${id.slice(0, SHORT_ID_LENGTH)}…` : id
    },

    async copyId(id) {
      try {
        await navigator.clipboard.writeText(id)
        await this.success('Идентификатор скопирован')
      } catch {
        await this.info(id)
      }
    },

    applyDates(range) {
      const [first, second] = [...range].sort()

      this.filters.dateFrom = first
      this.filters.dateTo = second || first

      if (range.length === 2) this.datesOpened = false
    },
    clearDates() {
      this.dates = []
      this.filters.dateFrom = null
      this.filters.dateTo = null
    },
    terminatePopupShow() {
      this.$refs.terminatePopup.open()
    },
  },
}
</script>

<style lang="scss" scoped>
#workflow-list {
  .workflow-list__totals {
    gap: 8px;
  }

  .workflow-list__id {
    font-family: monospace;
    cursor: pointer;
  }
}
</style>
